import * as React from 'react'
import { GetStaticProps } from 'next'
import { NextSeo } from 'next-seo'
import Image from 'next/image'
import Link from 'next/link'

import Container from '~/components/Container'
import Accordion from '~/components/Accordion'
import NotchedCard from '~/components/NotchedCard'
import GooglePlay from '~/svg/google-play-black.svg'
import AppleStore from '~/svg/apple-store-black.svg'

const title = 'Support - Mary AG'
const description = ''

const faqs = [
  {
    question: 'How much is a Mary, and when can I get it?',
    answer:
      'Mary is $1899 CAD or $1699 USD, free shipping. The unit will ship within 3 days or order. Orders for Canadian addresses take 1-3 days in transit. Orders for USA addresses take 7-10 days in transit. '
  },
  {
    question: 'Any pest, mould, smell?',
    answer:
      "Thought you'd ask! Mary has a filtration system built in. No smell, no pest, no mould. You can even dry and cure your herbs in it."
  },
  {
    question:
      'How automated is Mary anyway? What about water, nutrients and pH?',
    answer:
      'Mary will take care of almost everything, and maintain optimal light schedules for each phase of the grow. It will also help manage temperature while monitoring humidity, water levels, plant height, and a number of other factors that contribute to plant health. The app will alert you if any plant care is necessary and show you how it is done. For experienced growers, you can also use manual settings to manage how your plant grows.'
  },
  {
    question: 'How big is Mary? How many plants can I fit in there?',
    answer:
      "17.7x12.9x53.1 inches. It may look compact, but it grows a lot more than you'd expect because of our awesome innovations."
  }
]

export const getStaticProps: GetStaticProps = async () => {
  const data = {}
  return { props: { data } }
}

export default function SupportPage(props) {
  return (
    <Container className="flex-col">
      <NextSeo title={title} description={description} />
      <section className="max-w-7xl mx-auto px-6 pt-20 lg:pt-40 lg:-mb-20 xl:-mb-36">
        <div className="lg:w-1/2">
          <h2 className="text-white font-medium text-2xl lg:text-3xl xl:text-4xl">
            Mary Support
          </h2>
          <p className="text-white opacity-70 text-base xl:text-lg pt-12 lg:pt-14 xl:pt-20 pb-12 lg:pb-14 xl:pb-40">
            Looking to learn more about Mary? Read through our FAQs below or{' '}
            <Link href="/contact-us">
              <a className="underline">get in touch</a>
            </Link>{' '}
            if you can’t find your answer.
          </p>
        </div>
        <div className="flex flex-col lg:flex-row">
          <div className="hidden lg:block lg:w-full lg:-ml-6 xl:-ml-28 lg:pr-18 xl:pr-28">
            <NotchedCard notchPosition="top-right">
              <Image
                src="/images/support-page-header-image.jpg"
                alt="MaryAG Support"
                width={914}
                height={609}
                layout="responsive"
                objectFit="cover"
                className="aspect-w-3 aspect-h-2"
              />
            </NotchedCard>
          </div>
          <div className="lg:hidden -mx-6">
            <NotchedCard notchPosition="top-right">
              <Image
                src="/images/support-page-header-image-mobile.jpg"
                alt="MaryAG Support"
                width={320}
                height={480}
                layout="responsive"
                objectFit="cover"
                className="aspect-w-2 aspect-h-3"
              />
            </NotchedCard>
          </div>
          <div className="flex flex-col lg:w-1/3">
            <h3 className="text-white text-base pt-14 lg:pt-0">
              Support for Growers.
            </h3>
            <p className="text-white opacity-70 text-base pt-11 lg:pt-8 xl:pt-16">
              If you currently have a Mary Model Z, and need help with growing
              advice or troubleshooting, use Live Support in the Mary AG app for
              immediate help.
            </p>
            <div className="inline-flex gap-x-2.5 pt-7 lg:pt-8 xl:pt-16 pb-16 lg:pb-0">
              <Link href="https://apps.apple.com/app/mary/id1434968352">
                <a title="Apple Store | Mary AG" target="_blank">
                  <AppleStore className="w-[131px] h-[38px] lg:w-auto lg:h-auto" />
                </a>
              </Link>
              <Link href="https://play.google.com/store/apps/details?id=com.mary">
                <a title="Google Play Store | Mary AG" target="_blank">
                  <GooglePlay className="w-[131px] h-[38px] lg:w-auto lg:h-auto" />
                </a>
              </Link>
            </div>
          </div>
        </div>
      </section>
      <div className="bg-white lg:pt-64 xl:pt-80">
        <div className="bg-white px-6 py-16 text-grey-700 max-w-7xl mx-auto">
          <h3 className="text-xl font-medium md:text-center md:text-2xl">
            Frequently Asked Questions
          </h3>
          <div className="pt-9 lg:pt-20">
            {faqs.map(({ question, answer }, i) => (
              <div className="border-t border-grey-200 py-4" key={i}>
                <Accordion key={i} title={question} body={answer} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Container>
  )
}
